import { Alert, Box, Typography } from '@mui/material';
import { auditRanges, TimeRange } from '../utils/timeRanges';

/**
 * Footer for a cutoff schedule editor: states the boundary convention, and
 * warns about ranges that can never match or times nothing covers.
 *
 * Neither problem is visible in the rows themselves, and neither raises an
 * error — an uncovered time just falls back to the studio's flat value.
 */
export default function TimeRangeAudit({
  ranges,
  fallbackHours,
}: {
  ranges: TimeRange[];
  /** The flat cutoff uncovered times use. Its field is hidden while a schedule
   *  is in use, so name the number rather than point at something off screen. */
  fallbackHours?: number;
}) {
  const { unreachable, gaps } = auditRanges(ranges || []);
  if (!(ranges || []).length) return null;

  return (
    <Box>
      {/* Adjacent rows share a boundary, so both show 12:00 and it reads like an
          overlap. Without this line operators write 11:59, leaving that minute
          in no range at all. */}
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
        End time is exclusive. Use 12:00, not 11:59.
      </Typography>

      {(unreachable.length > 0 || gaps.length > 0) && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {unreachable.length > 0 && (
            <Typography variant="body2">
              {unreachable.length === 1 ? 'Range' : 'Ranges'}{' '}
              {unreachable.map(i => `#${i + 1}`).join(', ')}{' '}
              {unreachable.length === 1 ? 'never applies' : 'never apply'}. An earlier range covers
              that time.
            </Typography>
          )}
          {gaps.length > 0 && (
            <Typography variant="body2">
              Not covered: <strong>{gaps.join(', ')}</strong>. These times use the fixed cutoff
              {typeof fallbackHours === 'number' ? ` of ${fallbackHours}h` : ''}.
            </Typography>
          )}
        </Alert>
      )}
    </Box>
  );
}
